import { Button, Modal, Select } from '@automata/ui';
import { Box, FormControl, MenuItem, Typography } from '@mui/material';

import {
  type SchedulerVersionResolutionPromptProps,
  useSchedulerVersionModal,
} from './useSchedulerVersionModal';

export interface SchedulerVersionModalProps {
  currentVersion?: string;
  defaultSelectedVersion?: string;
  onConfirm?: (selectedVersion: string) => void;
}

export const SchedulerVersionModal = ({
  currentVersion,
  defaultSelectedVersion,
  onConfirm,
}: SchedulerVersionModalProps) => {
  const config: SchedulerVersionResolutionPromptProps | null =
    currentVersion && defaultSelectedVersion && onConfirm
      ? { currentVersion, defaultSelectedVersion, onConfirm }
      : null;

  const modalState = useSchedulerVersionModal(config);

  if (!modalState) return null;

  const {
    isOpen,
    currentVersion: modalCurrentVersion,
    availableVersions,
    selectedVersion,
    onVersionSelect,
    onConfirm: handleOnConfirm,
    isConfirmDisabled,
  } = modalState;

  return (
    <Modal
      open={isOpen}
      onClose={() => {}} // user shouldn't be able to close without selecting a version
      title="Scheduler version update required"
    >
      <Box display="flex" flexDirection="column" gap={3} sx={{ minWidth: 400 }}>
        <Typography variant="body1">
          The current scheduler version ({modalCurrentVersion}) is no longer
          supported. Please select a new version to continue:
        </Typography>

        <FormControl fullWidth>
          <Select
            labelId="version-select-label"
            value={selectedVersion}
            onChange={(event) => {
              if (typeof event.target.value === 'string')
                onVersionSelect(event.target.value);
            }}
            label="Select Scheduler Version"
          >
            {availableVersions.map((version) => (
              <MenuItem key={version} value={version}>
                {version}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary">
          If you need assistance or want to keep using an unsupported version,
          please contact Automata support.
        </Typography>

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleOnConfirm}
            color="primary"
            disabled={isConfirmDisabled}
          >
            Update Version
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
